import React, { useMemo, useState } from 'react'
import { Routes } from '@/assets/configs'
import { Constants } from '@/assets'
import { Box } from '@mui/material'
import { EmtButton, EmtModal, EmtTypography, EmtTextField, EmtTextarea } from 'emt-components-v2'
import { Validator } from '@/utils'
import { useNotifications } from '@/modules/Notifications'
import { usePetsitterUser } from '@/modules/PetsitterUser/hooks/usePetsitterUser'

import { askReviews } from '@/modules/Reviews/ReviewsServiceHelper'

interface AskReviewModalProps {
  open: boolean
  onClose: () => void
  maxContacts?: number
}
const initialMessage = `Bonjour,

Je viens de m'inscrire sur la plateforme Super Sitters pour proposer mes services de pet sitting. Les témoignages sont très importants pour donner confiance, si vous pouvez prendre une minute pour écrire un témoignage à propos de mon expérience avec les animaux et mes qualités cela me sera très utile.

Je vous remercie énormément !`

const AskReviewModal: React.FC<AskReviewModalProps> = ({ open, onClose, maxContacts = 10 }) => {
  const { displayName, id } = usePetsitterUser()
  const [addNotificationError] = useNotifications()
  const [isLoading, setIsLoading] = useState(false)
  const [formIsSend, setFormIsSend] = useState(false)
  const [successStep, setSuccessStep] = useState<boolean>(false)

  // champs dynamiques (emails)
  const [emails, setEmails] = useState<string[]>([''])
  const [emailsErrors, setEmailsErrors] = useState<string[]>([''])

  // message
  const [message, setMessage] = useState(initialMessage)
  const [messageError, setMessageError] = useState('')

  const normalizeEmail = (v: string) => v.trim().toLowerCase()

  const isEmailValid = (v: string) => Validator.validate(v, Validator.defaultValidateTypes.mail)

  const ensureExtraFieldIfNeeded = (nextEmails: string[]) => {
    if (nextEmails.length >= maxContacts) return nextEmails

    const last = normalizeEmail(nextEmails[nextEmails.length - 1] ?? '')
    // si le dernier est valide, on ajoute un champ vide
    if (last && isEmailValid(last)) {
      return [...nextEmails, '']
    }
    return nextEmails
  }

  const setEmailAt = (index: number, value: string) => {
    let next = [...emails]
    // If the field is erased and there is more than one field, remove it
    if (value === '' && next.length > 1) {
      next.splice(index, 1)
    } else {
      next[index] = value
    }
    const finalNext = ensureExtraFieldIfNeeded(next)
    setEmails(finalNext)

    // sync erreurs array
    setEmailsErrors((prev) => {
      let nextErr = [...prev]
      // Adjust errors array if a field was removed
      while (nextErr.length > finalNext.length) nextErr.pop()
      while (nextErr.length < finalNext.length) nextErr.push('')
      // validation live si le form a déjà été submit
      if (formIsSend) {
        // If a field was removed, validate all
        if (value === '' && emails.length > 1) {
          nextErr = finalNext.map((email, i) => validateEmailField(i, normalizeEmail(email), finalNext))
        } else {
          nextErr[index] = validateEmailField(index, normalizeEmail(value), finalNext)
        }
      }
      return nextErr
    })
  }

  const validateEmailField = (index: number, rawValue: string, currentEmails = emails): string => {
    const value = normalizeEmail(rawValue)

    // dernier champ vide autorisé (champ “d’ajout”)
    const isLast = index === currentEmails.length - 1
    if (isLast && !value) return ''

    if (!value) return 'Email requis'
    if (!isEmailValid(value)) return 'Email invalide'
    return ''
  }

  const validateMessageField = (raw: string): string => {
    const v = String(raw ?? '').trim()
    if (!v) return 'Message requis'
    return ''
  }

  const checkForm = () => {
    // emails : on ignore le dernier champ vide (champ d’ajout)
    const cleaned = emails.map(normalizeEmail)
    const withoutTrailingEmpty = cleaned.length > 1 && !cleaned[cleaned.length - 1] ? cleaned.slice(0, -1) : cleaned

    const nextEmailErrors = [...emailsErrors]
    while (nextEmailErrors.length < emails.length) nextEmailErrors.push('')

    let ok = true

    // au moins 1 email
    if (withoutTrailingEmpty.length === 0) {
      ok = false
      // on met l’erreur sur le premier champ
      nextEmailErrors[0] = 'Ajoutez au moins un email'
    }

    // valider chaque email non vide
    withoutTrailingEmpty.forEach((email, i) => {
      const err = validateEmailField(i, email, emails)
      nextEmailErrors[i] = err
      if (err) ok = false
    })

    // si on a un champ d’ajout vide à la fin, on le laisse sans erreur
    const msgErr = validateMessageField(message)
    if (msgErr) ok = false

    setEmailsErrors(nextEmailErrors)
    setMessageError(msgErr)

    return { ok, emailsToSend: withoutTrailingEmpty, messageToSend: message.trim() }
  }

  const isValid = useMemo(() => {
    const cleaned = emails.map(normalizeEmail)
    const withoutTrailingEmpty = cleaned.length > 1 && !cleaned[cleaned.length - 1] ? cleaned.slice(0, -1) : cleaned

    const hasAtLeastOne = withoutTrailingEmpty.length > 0
    const allValid = withoutTrailingEmpty.every((e) => e && isEmailValid(e))
    const msgOk = !!message.trim()

    return hasAtLeastOne && allValid && msgOk && !isLoading
  }, [emails, message, isLoading])

  const submit = async () => {
    setFormIsSend(true)
    const { ok, emailsToSend, messageToSend } = checkForm()
    if (!ok) return

    setIsLoading(true)
    try {
      // Call askReviews API
      await askReviews({
        content: messageToSend,
        emails: emailsToSend,
        url:
          Constants.node_env === 'production'
            ? Constants.rootUrl +
              Routes.Testimony +
              `?targetDisplayName=${displayName}&targetId=${id}&authorType=DOGOWNER`
            : 'https://staging.app.supersitters.app' +
              Routes.Testimony +
              `?targetDisplayName=${displayName}&targetId=${id}&authorType=DOGOWNER`,
      })
      setSuccessStep(true)
      // reset form
      setEmails([''])
      setMessage(initialMessage)
      setFormIsSend(false)
    } catch (error) {
      addNotificationError({
        error,
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <EmtModal
      variant="default"
      open={open}
      onClose={() => {
        setSuccessStep(false)
        onClose()
      }}
      sx={{ zIndex: 9999 }}
    >
      {successStep ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <EmtTypography variant="body1" fontWeight={700}>
            Vos demandes de témoignages ont bien été envoyées !
          </EmtTypography>
          <EmtButton fullWidth color="purple" variant="contained" onClick={onClose} disabled={isLoading}>
            Retour au profil
          </EmtButton>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <EmtTypography variant="h4" fontWeight={800}>
            Demander des avis
          </EmtTypography>

          <EmtTypography variant="body1" fontWeight={400}>
            Le plus de témoignages positifs vous recevez, le plus les propriétaires d&apos;animaux choisissent votre
            profil.
            <br />
            <br />
            Demandez à des personnes pour lesquelles vous avez déjà réalisé des services de pet sitting, des membres de
            votre famille, des amis, afin qu&apos;ils témoignent à propos de votre expérience avec les animaux et de vos
            qualités.
          </EmtTypography>

          <EmtTypography variant="body1" fontWeight={400}>
            <b>Vos contacts</b> (amis, famille, clients)
          </EmtTypography>

          {/* Champs emails dynamiques */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {emails.map((email, idx) => (
              <EmtTextField
                key={idx}
                fullWidth
                type="email"
                label={idx === 0 ? 'Email' : `Email`}
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmailAt(idx, e.target.value)}
                error={!!emailsErrors[idx]?.length}
                helperText={emailsErrors[idx]}
                onBlur={() => {
                  // validation au blur (utile même si pas submit)
                  setEmailsErrors((prev) => {
                    const next = [...prev]
                    while (next.length < emails.length) next.push('')
                    next[idx] = validateEmailField(idx, normalizeEmail(emails[idx] ?? ''), emails)
                    return next
                  })
                }}
                onKeyDown={(e) => e.key === 'Enter' && submit()}
              />
            ))}
          </Box>

          <EmtTypography variant="body1" fontWeight={400}>
            <b>Votre message :</b>
          </EmtTypography>

          <EmtTextarea
            label="Votre message"
            hideLabelOnClick
            maxLength={1000}
            value={message}
            onChange={(value) => {
              setMessage(value)
              if (formIsSend) setMessageError(validateMessageField(value))
            }}
            error={!!messageError?.length}
            helperText={messageError}
            sx={{ minHeight: 180 }}
            preventScroll
          />

          <Box sx={{ display: 'flex', gap: '20px' }}>
            <EmtButton fullWidth color="purple" variant="outlined" onClick={onClose} disabled={isLoading}>
              Retour
            </EmtButton>
            <EmtButton
              fullWidth
              color="purple"
              variant="contained"
              onClick={submit}
              loader={isLoading}
              disabled={!isValid}
            >
              Envoyer
            </EmtButton>
          </Box>

          <EmtTypography variant="body2" fontWeight={400} color="#34303E">
            Vos contacts recevront par email votre message ainsi qu&apos;un lien pour écrire leur témoignage, même
            s&apos;ils ne sont pas inscrits sur Super Sitters.
          </EmtTypography>
        </Box>
      )}
    </EmtModal>
  )
}

export default AskReviewModal
